import { useEffect, useState } from "react"
import { useParams, Link } from "react-router-dom"
import { motion } from "framer-motion"
import { ArrowLeft, Package, Search } from "lucide-react"
import { api, type Category, type Product } from "@/lib/api"
import { ProductCard } from "@/components/product-card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useLanguage } from "@/lib/language-context"

export default function CategoryDetailPage() {
    const { slug } = useParams<{ slug: string }>()
    const { t } = useLanguage()
    const [category, setCategory] = useState<Category | null>(null)
    const [products, setProducts] = useState<Product[]>([])
    const [loading, setLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState("")
    const [sortBy, setSortBy] = useState("newest")

    useEffect(() => {
        const fetchData = async () => {
            if (!slug) return
            try {
                setLoading(true)
                // Fetch category details
                const categoryData = await api.getCategoryBySlug(slug)
                setCategory(categoryData)

                // Fetch products for this category
                if (categoryData) {
                    const productsData = await api.getProducts({ category: categoryData.id })
                    setProducts(productsData || [])
                }
            } catch (error) {
                console.error("Error fetching category:", error)
            } finally {
                setLoading(false)
            }
        }

        fetchData()
    }, [slug])

    // Filter and sort products
    const filteredProducts = products
        .filter((product) => {
            if (searchQuery && !product.name.toLowerCase().includes(searchQuery.toLowerCase())) {
                return false
            }
            return true
        })
        .sort((a, b) => {
            switch (sortBy) {
                case "price_low":
                    return Number(a.price) - Number(b.price)
                case "price_high":
                    return Number(b.price) - Number(a.price)
                case "name_az":
                    return a.name.localeCompare(b.name)
                case "name_za":
                    return b.name.localeCompare(a.name)
                case "newest":
                default:
                    return b.id - a.id
            }
        })

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-950 dark:to-gray-900">
                <div className="container mx-auto px-4 py-8">
                    <div className="h-8 w-48 bg-slate-200 dark:bg-gray-800 rounded-lg animate-pulse mb-8" />
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="bg-slate-200 dark:bg-gray-800 animate-pulse rounded-xl h-80" />
                        ))}
                    </div>
                </div>
            </div>
        )
    }

    if (!category) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-950 dark:to-gray-900">
                <div className="w-24 h-24 bg-slate-200 dark:bg-gray-800 rounded-full flex items-center justify-center">
                    <Package className="h-12 w-12 text-muted-foreground" />
                </div>
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Kategoriya topilmadi</h2>
                    <p className="text-muted-foreground">Bu kategoriya mavjud emas</p>
                </div>
                <Link to="/categories">
                    <Button className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Kategoriyalarga qaytish
                    </Button>
                </Link>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
            {/* Hero Header */}
            <div className="relative overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src={category.image_url || category.image || "/placeholder.svg"}
                        alt={category.name}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-teal-800/80" />
                </div>

                {/* Content */}
                <div className="relative container mx-auto px-4 py-16 md:py-24">
                    <Link
                        to="/categories"
                        className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors mb-6 group"
                    >
                        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                        <span>Kategoriyalarga qaytish</span>
                    </Link>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <Badge className="bg-white/20 text-white border-0 mb-4">
                            <Package className="h-3 w-3 mr-1" />
                            {products.length} ta mahsulot
                        </Badge>
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                            {category.name}
                        </h1>
                        {category.description && (
                            <p className="text-white/80 text-lg max-w-2xl">
                                {category.description}
                            </p>
                        )}
                    </motion.div>
                </div>
            </div>

            {/* Products Section */}
            <div className="container mx-auto px-4 py-8">
                {/* Toolbar */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="flex flex-col sm:flex-row gap-4 mb-8 bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm"
                >
                    {/* Search */}
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Mahsulotlarni qidirish..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 bg-slate-50 dark:bg-gray-700 border-0"
                        />
                    </div>

                    {/* Sort */}
                    <Select value={sortBy} onValueChange={setSortBy}>
                        <SelectTrigger className="w-full sm:w-48 bg-slate-50 dark:bg-gray-700 border-0">
                            <SelectValue placeholder="Saralash" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="newest">Eng yangi</SelectItem>
                            <SelectItem value="price_low">Arzon → Qimmat</SelectItem>
                            <SelectItem value="price_high">Qimmat → Arzon</SelectItem>
                            <SelectItem value="name_az">A → Z</SelectItem>
                            <SelectItem value="name_za">Z → A</SelectItem>
                        </SelectContent>
                    </Select>
                </motion.div>

                {/* Products Grid */}
                {filteredProducts.length > 0 ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                    >
                        {filteredProducts.map((product, index) => (
                            <motion.div
                                key={product.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: index * 0.05 }}
                            >
                                <ProductCard product={product} />
                            </motion.div>
                        ))}
                    </motion.div>
                ) : (
                    <div className="text-center py-16">
                        <div className="w-24 h-24 bg-slate-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Search className="h-10 w-10 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                            Mahsulotlar topilmadi
                        </h3>
                        <p className="text-muted-foreground mb-4">
                            {searchQuery ? "Boshqa qidiruv so'zini sinab ko'ring" : "Bu kategoriyada hozircha mahsulotlar yo'q"}
                        </p>
                        {searchQuery && (
                            <Button variant="outline" onClick={() => setSearchQuery("")}>
                                Qidiruvni tozalash
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
